import React, { useState } from "react";
import { Pressable, ScrollView, View } from "react-native";
import CCText from "@/Atoms/CCText";
import CCLoader from "@/Atoms/CCLoader";
import { useMediaQuery } from "@/Hooks/useMediaQuery/useMediaQuery";
import BackToPrevious from "@/Atoms/BackToPrevious";
import { useDivisionStandings } from "@/Hooks/ClubsApi/useClubsQueries";
import ClubBadge from "./components/ClubBadge";
import { BADGES } from "./badgeOptions";
import type { PieceKey } from "./clubPieces";
import type { StandingsRow, LadderRow } from "@/Hooks/ClubsApi/types";

// A standings row's `piece` is the club's badge_id; map it to a chess piece the
// same way ClubRoom does. The ladder's `piece` is already a piece key.
const pieceForBadge = (badgeId?: string): PieceKey =>
  (BADGES.find((b) => b.id === badgeId)?.piece ?? "pawn") as PieceKey;

// "promotes in Xd Yh" — from seconds to the next Monday 00:00 IST.
function countdown(seconds: number): string {
  if (seconds <= 0) return "now";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const rowTint = (r: StandingsRow) =>
  r.is_mine
    ? { backgroundColor: "#FDF1C8", borderWidth: 1.5, borderColor: "#FACF47" }
    : r.zone === "promo"
    ? { backgroundColor: "#EAF7EF" }
    : r.zone === "releg"
    ? { backgroundColor: "#FDECEE" }
    : {
        backgroundColor: "#fff",
        shadowColor: "#4D3F37",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
      };

function ZoneLabel({ kind }: { kind: "promo" | "releg" }) {
  const promo = kind === "promo";
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginTop: 4 }}>
      <CCText
        style={{
          fontSize: 11,
          fontWeight: "700",
          color: promo ? "#1E7A3D" : "#CD2C39",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {promo ? "▲ Promotion zone" : "▼ Relegation zone"}
      </CCText>
    </View>
  );
}

function StandingRow({ row }: { row: StandingsRow }) {
  return (
    <View
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 11,
          padding: 11,
          borderRadius: 12,
        },
        rowTint(row),
      ]}
    >
      <CCText style={{ width: 24, textAlign: "center", fontWeight: "700", color: "#806234", fontSize: 14 }}>
        {row.rank}
      </CCText>
      <ClubBadge piece={pieceForBadge(row.piece)} color={row.background_color} size={34} radius={10} />
      <CCText numberOfLines={1} style={{ flex: 1, fontSize: 14, fontWeight: "600", color: "#262322" }}>
        {row.name}
        {row.private ? <CCText style={{ fontSize: 11, color: "#A8A7A6", fontWeight: "500" }}> · private</CCText> : null}
      </CCText>
      <CCText style={{ fontSize: 14, fontWeight: "700", color: "#4D3F37" }}>
        {row.points.toLocaleString("en-IN")}
      </CCText>
    </View>
  );
}

function Ladder({ rows }: { rows: LadderRow[] }) {
  return (
    <View>
      <CCText
        style={{
          fontSize: 11,
          fontWeight: "700",
          letterSpacing: 0.5,
          textTransform: "uppercase",
          color: "#888280",
          marginBottom: 10,
        }}
      >
        Division ladder
      </CCText>
      <View style={{ gap: 7 }}>
        {rows.map((l) => (
          <View
            key={l.level}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              paddingVertical: 9,
              paddingHorizontal: 11,
              borderRadius: 10,
              backgroundColor: l.here ? "#F0FAF8" : "#fff",
              borderWidth: l.here ? 1.5 : 1,
              borderColor: l.here ? "#3DAB9E" : "rgba(38,35,34,0.10)",
            }}
          >
            <ClubBadge piece={l.piece as PieceKey} color="#6B584D" size={26} radius={7} pieceColor="black" />
            <CCText style={{ flex: 1, fontSize: 13.5, fontWeight: "600", color: "#262322" }}>
              {l.name} Division
            </CCText>
            {l.here ? (
              <View style={{ backgroundColor: "#E0F2EF", borderRadius: 999, paddingHorizontal: 7, paddingVertical: 2 }}>
                <CCText style={{ fontSize: 10.5, fontWeight: "700", color: "#3DAB9E" }}>You are here</CCText>
              </View>
            ) : null}
            <CCText style={{ fontSize: 12, color: "#888280" }}>{l.count} clubs</CCText>
          </View>
        ))}
      </View>
    </View>
  );
}

export default function ClubStandings({ navigation, route }: any) {
  const clubId: number | undefined = route.params?.clubId;
  const startLevel: number = route.params?.level ?? 0;
  const [period, setPeriod] = useState<"today" | "week">("week");
  const { isMobileOrTab } = useMediaQuery();
  const isWide = !isMobileOrTab;
  const { data, isLoading } = useDivisionStandings(startLevel, period, clubId);

  const promoRows = (data?.rows ?? []).filter((r) => r.zone === "promo");
  const midRows = (data?.rows ?? []).filter((r) => r.zone === "");
  const relegRows = (data?.rows ?? []).filter((r) => r.zone === "releg");

  const Toggle = () => (
    <View style={{ flexDirection: "row", gap: 6 }}>
      {(["today", "week"] as const).map((p) => {
        const active = period === p;
        return (
          <Pressable
            key={p}
            testID={`std-period-${p}`}
            onPress={() => setPeriod(p)}
            style={{
              paddingVertical: 8,
              paddingHorizontal: 14,
              borderRadius: 999,
              backgroundColor: active ? "#3DAB9E" : "#fff",
              borderWidth: active ? 0 : 1,
              borderColor: "rgba(38,35,34,0.12)",
            }}
          >
            <CCText style={{ fontWeight: "600", color: active ? "#fff" : "#6B584D", fontSize: 13 }}>
              {p === "today" ? "Today" : "This Week"}
            </CCText>
          </Pressable>
        );
      })}
    </View>
  );

  if (isLoading || !data) return <CCLoader loading={true} />;
  const div = data.division;

  const Race = () => (
    <View style={{ gap: 6 }}>
      {promoRows.length ? <ZoneLabel kind="promo" /> : null}
      {promoRows.map((r) => (
        <StandingRow key={r.club_id} row={r} />
      ))}
      {midRows.map((r) => (
        <StandingRow key={r.club_id} row={r} />
      ))}
      {relegRows.length ? <ZoneLabel kind="releg" /> : null}
      {relegRows.map((r) => (
        <StandingRow key={r.club_id} row={r} />
      ))}
      <CCText style={{ fontSize: 11.5, color: "#A8A7A6", textAlign: "center", marginTop: 8 }}>
        Official clubs don't compete in standings.
      </CCText>
    </View>
  );

  return (
    <View style={{ flex: 1, backgroundColor: "#FAF6EB" }}>
      <View style={{ paddingHorizontal: isWide ? 32 : 16, paddingTop: isWide ? 24 : 8 }}>
        {/* navigation.goBack() is unreliable in this app — MainMenu resets the
            drawer's history on every sidebar click, which left this button
            going nowhere or somewhere stale after that. BackToPrevious tracks
            the real previous route itself (see its own header comment) rather
            than trusting react-navigation's back stack. Falls back to the
            Clubs list (not ClubRoom): with no recorded history there's no
            known clubId to route back into a specific room with. */}
        <BackToPrevious
          navigation={navigation}
          fallback="Clubs"
          style={{ alignSelf: "flex-start", marginBottom: 8 }}
        />
        <CCText fontWeight="thunder" style={{ fontSize: isWide ? 30 : 26, color: "#262322" }}>
          {`♞ ${div.name} Division`}
        </CCText>
        <CCText style={{ fontSize: isWide ? 13.5 : 13, color: "#888280", marginTop: 2 }}>
          {div.club_count} clubs · promotes in {countdown(div.promotes_in_seconds)}
        </CCText>
      </View>

      {isWide ? (
        <View style={{ flex: 1, flexDirection: "row", minHeight: 0, paddingHorizontal: 32, paddingTop: 16, gap: 24 }}>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ maxWidth: 720, paddingBottom: 28 }}>
            <View style={{ marginBottom: 12 }}>
              <Toggle />
            </View>
            <Race />
          </ScrollView>
          <ScrollView style={{ width: 300 }} contentContainerStyle={{ paddingBottom: 28 }}>
            <Ladder rows={data.ladder} />
          </ScrollView>
        </View>
      ) : (
        <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 12, paddingBottom: 30 }}>
          <View style={{ marginBottom: 6 }}>
            <Toggle />
          </View>
          <Race />
          <View style={{ marginTop: 18 }}>
            <Ladder rows={data.ladder} />
          </View>
        </ScrollView>
      )}
    </View>
  );
}
